import { inject, Injectable } from '@angular/core'
import { defer, forkJoin, map, Observable, throwError } from 'rxjs'
import { convertKeysCamelToSnake } from '../../utils'
import { Account, AccountLimit, AccountLink, AccountSecuritiesSettings, AccountsCount, BillingBase, ClosedPosition, CommonAccountSettings, CommonDivisionSettings, CreateAccountRequest, CurrencyResponse, DayBalance, EventResponse, ExchBalance, Exchange, FullOperationInfo, Message, MessageEditResult, MessageType, OpType, OpenedPosition, OperationData, Order, RawOperation, Security, SettingAction, SettingDescription, Trade, TradeMetricReport, TradeResultReport, TradingInfo, UnknownTrade } from './trade-system.model'
import { RequestOptions, TsName, TsNameEnum, TsProxyService } from './ts-proxy.service'
import { PropService } from '../prop/prop.service'

export interface TsData<T> {
  tsName: TsName
  data: T
}

@Injectable({
  providedIn: 'root',
})
export class TradeSystemService {
  private proxy = inject(TsProxyService)
  private prop = inject(PropService)

  for(ts: TsName | TsNameEnum): TradeSystemApi {
    return new TradeSystemApi(this.proxy, ts)
  }

  forAll<T>(run: (api: TradeSystemApi) => Observable<T>): Observable<TsData<T>[]> {
    return defer(() => {
      const list = this.prop.currentProp?.ts ?? []
      if (list.length === 0) {
        return throwError(() => new Error('No trade systems available'))
      }
      const requests = list.map(ts => run(this.for(ts)).pipe(
        map(data => ({ tsName: ts, data }))
      ))
      return forkJoin(requests)
    })
  }
}

class TradeSystemApi {
  constructor(private proxy: TsProxyService, public readonly tsName: TsName) {}

  /**
   * Получить случайное свободное имя для торгового счета.
   * GET /api/v1.0/Accounts/trading/generate
   */
  generateTradingAccountName(params?: { length?: number }, options?: RequestOptions): Observable<string> {
    return this.proxy.get(this.tsName, '/Accounts/trading/generate', {
      length: params?.length,
    }, options)
  }

  /**
   * Получить список имен действующих торговых счетов.
   * GET /api/v1.0/Accounts/trading
   */
  getTradingAccounts(params?: { withDeleted?: boolean }, options?: RequestOptions): Observable<string[]> {
    return this.proxy.get(this.tsName, '/Accounts/trading', {
      with_deleted: params?.withDeleted,
    }, options)
  }

  /**
   * Удалить торговый счет.
   * DELETE /api/v1.0/Accounts/trading
   */
  deleteTradingAccount(params: { accName: string }): Observable<void> {
    return this.proxy.delete(this.tsName, '/Accounts/trading', {
      acc_name: params.accName,
    })
  }

  /**
   * Получить связки текущих счетов со счетами в старых системах.
   * GET /api/v1.0/Accounts/trading/links
   */
  getTradingAccountLinks(params?: { withDeleted?: boolean }, options?: RequestOptions): Observable<AccountLink[]> {
    return this.proxy.get(this.tsName, '/Accounts/trading/links', {
      with_deleted: params?.withDeleted,
    }, options)
  }

  /**
   * Установка/изменение связи торгового счета с демо-счетами старых систем.
   * PUT /api/v1.0/Accounts/trading/demo-link
   */
  setTradingAccountDemoLink(params: { accName?: string; oldDemoLink?: string }): Observable<void> {
    return this.proxy.put(this.tsName, '/Accounts/trading/demo-link', undefined, {
      acc_name: params.accName,
      old_demo_link: params.oldDemoLink,
    })
  }

  /**
   * Установка/изменение связи торгового счета с боевыми счетами старых систем.
   * PUT /api/v1.0/Accounts/trading/real-link
   */
  setTradingAccountRealLink(params: { accName?: string; oldRealLink?: string }): Observable<void> {
    return this.proxy.put(this.tsName, '/Accounts/trading/real-link', undefined, {
      acc_name: params.accName,
      old_real_link: params.oldRealLink,
    })
  }

  /**
   * Создать торговый счет с данными трейдера.
   * POST /api/v1.0/Accounts/create-account
   * @deprecated Не используется в РМ
   */
  createAccount(params: CreateAccountRequest): Observable<string> {
    return this.proxy.post(this.tsName, '/Accounts/create-account', convertKeysCamelToSnake(params))
  }

  /**
   * Создать торговый счет.
   * PUT /api/v1.0/Accounts/trading/create
   * @deprecated Не используется на фронте, так как пароль генерируется автоматически сервером
   */
  createTradingAccount(params: { accName: string; password: string; startAssets?: number; currency?: string }): Observable<void> {
    return this.proxy.put(this.tsName, '/Accounts/trading/create', undefined, {
      acc_name: params.accName,
      password: params.password,
      start_assets: params.startAssets,
      currency: params.currency,
    })
  }

  /**
   * Изменить пароль торгового счета.
   * PUT /api/v1.0/Accounts/trading/change-pwd
   * @deprecated Не используется на фронте, так как пароль генерируется автоматически сервером
   */
  changeTradingAccountPassword(params: { accName: string; newPassword: string }): Observable<void> {
    return this.proxy.put(this.tsName, '/Accounts/trading/change-pwd', undefined, {
      acc_name: params.accName,
      new_password: params.newPassword,
    })
  }

  /**
   * Получить полные данные по торговым счетам.
   * GET /api/v1.0/Accounts/trading/details
   */
  getTradingAccountDetails(
    params?: {
      accName?: string
      withDeleted?: boolean
      withBalances?: boolean
      withSettings?: boolean
      withFirstTrades?: boolean
      limit?: number
      afterAccount?: string
      updatedAfter?: string
    },
    options?: RequestOptions
  ): Observable<Account[]> {
    return this.proxy.get(this.tsName, '/Accounts/trading/details', {
      acc_name: params?.accName,
      with_deleted: params?.withDeleted,
      with_balances: params?.withBalances,
      with_settings: params?.withSettings,
      with_first_trades: params?.withFirstTrades,
      limit: params?.limit,
      after_account: params?.afterAccount,
      updated_after: params?.updatedAfter,
    }, options)
  }

  /**
   * Получить список настроек торгового счета по инструментам биржи.
   * GET /api/v1.0/Accounts/trading/secsett
   */
  getTradingAccountSecuritiesSettings(
    params: { accName: string; exchCode: string },
    options?: RequestOptions
  ): Observable<AccountSecuritiesSettings[]> {
    return this.proxy.get(this.tsName, '/Accounts/trading/secsett', {
      acc_name: params.accName,
      exch_code: params.exchCode,
    }, options)
  }

  /**
   * Получить список имен действующих внутренних счетов.
   * GET /api/v1.0/Accounts/system
   */
  getSystemAccounts(options?: RequestOptions): Observable<string[]> {
    return this.proxy.get(this.tsName, '/Accounts/system', undefined, options)
  }

  /**
   * Получить полные данные по внутренним счетам.
   * GET /api/v1.0/Accounts/system/details
   */
  getSystemAccountDetails(params?: { accName?: string }, options?: RequestOptions): Observable<Account[]> {
    return this.proxy.get(this.tsName, '/Accounts/system/details', {
      acc_name: params?.accName,
    }, options)
  }

  /**
   * Создать счет подразделения.
   * PUT /api/v1.0/Accounts/division/create
   */
  createDivisionAccount(params: { accName: string }): Observable<void> {
    return this.proxy.put(this.tsName, '/Accounts/division/create', undefined, {
      acc_name: params.accName,
    })
  }

  /**
   * Удалить подразделение.
   * DELETE /api/v1.0/Accounts/division
   */
  deleteDivisionAccount(params: { divName: string }): Observable<void> {
    return this.proxy.delete(this.tsName, '/Accounts/division', {
      div_name: params.divName,
    })
  }

  /**
   * Получить полные данные по счетам подразделений.
   * GET /api/v1.0/Accounts/division/details
   */
  getDivisionAccountDetails(
    params?: { accName?: string; withBalances?: boolean; withSettings?: boolean;  withDeleted?: boolean },
    options?: RequestOptions
  ): Observable<Account[]> {
    return this.proxy.get(this.tsName, '/Accounts/division/details', {
      acc_name: params?.accName,
      with_balances: params?.withBalances,
      with_deleted: params?.withDeleted,
      with_settings: params?.withSettings,
    }, options)
  }

  /**
   * Получить список финансовых ограничений по счетам.
   * GET /api/v1.0/Accounts/limits
   */
  getAccountLimits(
    params?: { exchCode?: string; currCode?: string; updatedFrom?: string },
    options?: RequestOptions
  ): Observable<AccountLimit[]> {
    return this.proxy.get(this.tsName, '/Accounts/limits', {
      exch_code: params?.exchCode,
      curr_code: params?.currCode,
      updated_from: params?.updatedFrom,
    }, options)
  }

  /**
   * Получить базы расчета по биллингу за период.
   * GET /api/v1.0/Billing/base
   */
  getBillingBase(params: { beginDt: string; endDt: string; exchCode?: string }, options?: RequestOptions): Observable<BillingBase[]> {
    return this.proxy.get(this.tsName, '/Billing/base', {
      begin_dt: params.beginDt,
      end_dt: params.endDt,
      exch_code: params.exchCode,
    }, options)
  }

  /**
   * Получить количество уникально активных счетов за период.
   * GET /api/v1.0/Billing/active-count
   */
  getBillingActiveCount(
    params: { beginDt: string; endDt: string; exchCode?: string },
    options?: RequestOptions
  ): Observable<AccountsCount[]> {
    return this.proxy.get(this.tsName, '/Billing/active-count', {
      begin_dt: params.beginDt,
      end_dt: params.endDt,
      exch_code: params.exchCode,
    }, options)
  }

  /**
   * Снятие всех заявок счета на бирже.
   * POST /api/v1.0/Commands/orders/cancel-byacc
   */
  cancelOrdersByAccount(params: { accName: string; exchCode: string }): Observable<void> {
    return this.proxy.post(this.tsName, '/Commands/orders/cancel-byacc', undefined, {
      acc_name: params.accName,
      exch_code: params.exchCode,
    })
  }

  /**
   * Снятие одиночной заявки по номеру.
   * POST /api/v1.0/Commands/orders/cancel-single
   */
  cancelSingleOrder(params: { orderSnum?: string; exchCode?: string; secCode?: string; secBoard?: string; orderNum?: number }): Observable<void> {
    return this.proxy.post(this.tsName, '/Commands/orders/cancel-single', undefined, {
      order_snum: params.orderSnum,
      exch_code: params.exchCode,
      sec_code: params.secCode,
      sec_board: params.secBoard,
      order_num: params.orderNum,
    })
  }

  /**
   * Закрытие всех позиций счета на бирже.
   * POST /api/v1.0/Commands/positions/close-byacc
   */
  closePositionsByAccount(params: { accName: string; exchCode: string }): Observable<void> {
    return this.proxy.post(this.tsName, '/Commands/positions/close-byacc', undefined, {
      acc_name: params.accName,
      exch_code: params.exchCode,
    })
  }

  /**
   * Закрытие позиции по идентификатору.
   * POST /api/v1.0/Commands/positions/close-single
   */
  closeSinglePosition(params: { uid: string }): Observable<void> {
    return this.proxy.post(this.tsName, '/Commands/positions/close-single', undefined, {
      uid: params.uid,
    })
  }

  /**
   * Получить доступные системные валюты расчетов.
   * GET /api/v1.0/Currencies
   */
  getCurrencies(options?: RequestOptions): Observable<CurrencyResponse[]> {
    return this.proxy.get(this.tsName, '/Currencies', undefined, options)
  }

  /**
   * Добавить торговый счет к подразделению на площадке.
   * PUT /api/v1.0/Divisions/subaccount
   */
  addDivisionSubaccount(params: { divName: string; exchCode: string; tradeAccName: string }): Observable<void> {
    return this.proxy.put(this.tsName, '/Divisions/subaccount', undefined, {
      div_name: params.divName,
      exch_code: params.exchCode,
      trade_acc_name: params.tradeAccName,
    })
  }

  /**
   * Удалить торговый счет из подразделения на площадке.
   * DELETE /api/v1.0/Divisions/subaccount
   */
  removeDivisionSubaccount(params: { divName: string; exchCode: string; tradeAccName: string }): Observable<void> {
    return this.proxy.delete(this.tsName, '/Divisions/subaccount', {
      div_name: params.divName,
      exch_code: params.exchCode,
      trade_acc_name: params.tradeAccName,
    })
  }

  /**
   * Получить события торговой системы за период.
   * GET /api/v1.0/Events
   */
  getEvents(
    params?: { exchCode?: string; beginDt?: string; endDt?: string },
    options?: RequestOptions
  ): Observable<EventResponse[]> {
    return this.proxy.get(this.tsName, '/Events', {
      exch_code: params?.exchCode,
      begin_dt: params?.beginDt,
      end_dt: params?.endDt,
    }, options)
  }

  /**
   * Получить полные данные по биржам.
   * GET /api/v1.0/Exchanges
   */
  getExchanges(options?: RequestOptions): Observable<Exchange[]> {
    return this.proxy.get(this.tsName, '/Exchanges', undefined, options)
  }

  /**
   * Добавить биржу для подключения клиента.
   * PUT /api/v1.0/Exchanges/client/add
   */
  addExchangeClient(params: { email: string; exchCode?: string }): Observable<void> {
    return this.proxy.put(this.tsName, '/Exchanges/client/add', undefined, {
      email: params.email,
      exch_code: params.exchCode,
    })
  }

  /**
   * Удалить биржу из доступных к подключению у клиента.
   * DELETE /api/v1.0/Exchanges/client/remove
   */
  removeExchangeClient(params: { email: string; exchCode?: string }): Observable<void> {
    return this.proxy.delete(this.tsName, '/Exchanges/client/remove', {
      email: params.email,
      exch_code: params.exchCode,
    })
  }

  /**
   * Получить полные данные по инструментам.
   * GET /api/v1.0/Securities
   */
  getSecurities(params?: { exchCode?: string; updatedAfter?: string }, options?: RequestOptions): Observable<Security[]> {
    return this.proxy.get(this.tsName, '/Securities', {
      exch_code: params?.exchCode,
      updated_after: params?.updatedAfter,
    }, options)
  }

  /**
   * Получить полные данные по инструменту.
   * GET /api/v1.0/Securities/single
   */
  getSecurity(
    params: { exchCode: string; secCode: string; secBoard: string },
    options?: RequestOptions
  ): Observable<Security> {
    return this.proxy.get(this.tsName, '/Securities/single', {
      exch_code: params.exchCode,
      sec_code: params.secCode,
      sec_board: params.secBoard,
    }, options)
  }

  /**
   * Получить текущий биржи по валюте.
   * GET /api/v1.0/ExchBalances/current
   */
  getExchBalanceCurrent(params: { exchCode: string; currCode: string }, options?: RequestOptions): Observable<ExchBalance> {
    return this.proxy.get(this.tsName, '/ExchBalances/current', {
      exch_code: params.exchCode,
      curr_code: params.currCode,
    }, options)
  }

  /**
   * Получить динамику баланса биржи по валюте за период.
   * GET /api/v1.0/ExchBalances/history
   */
  getExchBalanceHistory(
    params: { exchCode: string; currCode: string; beginDt?: string; endDt?: string },
    options?: RequestOptions
  ): Observable<ExchBalance[]> {
    return this.proxy.get(this.tsName, '/ExchBalances/history', {
      exch_code: params.exchCode,
      curr_code: params.currCode,
      begin_dt: params.beginDt,
      end_dt: params.endDt,
    }, options)
  }

  /**
   * Получить список последних отправленных сообщений
   * GET /api/v1.0/Messaging
   */
  getMessages(params?: { withDeleted?: boolean }, options?: RequestOptions): Observable<Message[]> {
    return this.proxy.get(this.tsName, '/Messaging', {
      with_deleted: params?.withDeleted,
    }, options)
  }

  /**
   * Отправить новое сообщение
   * POST /api/v1.0/Messaging
   */
  sendMessage(params: { title: string; body: string; type: MessageType; accounts: string[] }): Observable<string> {
    return this.proxy.post(this.tsName, '/Messaging', params.accounts, {
      msg_title: params.title,
      msg_body: params.body,
      msg_type: params.type,
    })
  }

  /**
   * Изменить заголовок и текст сообщения
   * PUT /api/v1.0/Messaging
   */
  editMessage(params: { id: string; title: string; body: string }): Observable<MessageEditResult> {
    return this.proxy.put(this.tsName, '/Messaging', undefined, {
      msg_id: params.id,
      msg_title: params.title,
      msg_body: params.body,
    })
  }

  /**
   * Удалить сообщение
   * DELETE /api/v1.0/Messaging
   */
  deleteMessage(params: { id: string }): Observable<boolean> {
    return this.proxy.delete(this.tsName, '/Messaging', {
      msg_id: params.id,
    })
  }

  /**
   * Получить список операций по счету за период.
   * GET /api/v1.0/Operations
   */
  getOperations(
    params: { accName: string; currency: string; beginDt: string; endDt: string },
    options?: RequestOptions
  ): Observable<OperationData> {
    return this.proxy.get(this.tsName, '/Operations', {
      acc_name: params.accName,
      currency: params.currency,
      begin_dt: params.beginDt,
      end_dt: params.endDt,
    }, options)
  }

  /**
   * Получить список детализованных операций между счетами за период.
   * GET /api/v1.0/Operations/raw
   */
  getRawOperations(
    params: { beginDt: string; endDt: string; limit?: number },
    options?: RequestOptions
  ): Observable<RawOperation[]> {
    return this.proxy.get(this.tsName, '/Operations/raw', {
      begin_dt: params.beginDt,
      end_dt: params.endDt,
      limit: params.limit,
    }, options)
  }

  /**
   * Получить балансы счетов на определенную дату.
   * GET /api/v1.0/Operations/balance
   */
  getOperationsBalance(params: { dt: string }, options?: RequestOptions): Observable<DayBalance[]> {
    return this.proxy.get(this.tsName, '/Operations/balance', {
      dt: params.dt,
    }, options)
  }

  /**
   * Пополнение счета.
   * POST /api/v1.0/Operations/money/input
   */
  moneyInput(params: { toAccount: string; value: number; currency: string }): Observable<void> {
    return this.proxy.post(this.tsName, '/Operations/money/input', undefined, {
      to_account: params.toAccount,
      value: params.value,
      currency: params.currency,
    })
  }

  /**
   * Вывод средств со счета.
   * POST /api/v1.0/Operations/money/output
   */
  moneyOutput(params: { fromAccount: string; value: number; currency: string }): Observable<void> {
    return this.proxy.post(this.tsName, '/Operations/money/output', undefined, {
      from_account: params.fromAccount,
      value: params.value,
      currency: params.currency,
    })
  }

  /**
   * Перевод средств со счета на счет.
   * POST /api/v1.0/Operations/money/transfer
   */
  moneyTransfer(params: { fromAccount: string; toAccount: string; value: number; currency: string; description?: string }): Observable<void> {
    return this.proxy.post(this.tsName, '/Operations/money/transfer', undefined, {
      from_account: params.fromAccount,
      to_account: params.toAccount,
      value: params.value,
      currency: params.currency,
      description: params.description,
    })
  }

  /**
   * Получить список типов операций.
   * GET /api/v1.0/Operations/types
   */
  getOperationTypes(options?: RequestOptions): Observable<OpType[]> {
    return this.proxy.get(this.tsName, '/Operations/types', undefined, options)
  }

  /**
   * Получить неторговые операции за период.
   * GET /api/v1.0/Reports/non-trading-ops
   */
  getReportNonTradingOps(params?: { beginDt?: string; endDt?: string }, options?: RequestOptions): Observable<FullOperationInfo[]> {
    return this.proxy.get(this.tsName, '/Reports/non-trading-ops', {
      begin_dt: params?.beginDt,
      end_dt: params?.endDt,
    }, options)
  }

  /**
   * Получить торговые метрики (оборот, количество сделок).
   * GET /api/v1.0/Reports/trade-metrics
   */
  getReportTradeMetrics(
    params: { exchCode: string; beginDt?: string; endDt?: string },
    options?: RequestOptions
  ): Observable<TradeMetricReport[]> {
    return this.proxy.get(this.tsName, '/Reports/trade-metrics', {
      exch_code: params.exchCode,
      begin_dt: params.beginDt,
      end_dt: params.endDt,
    }, options)
  }

  /**
   * Получить торговые результаты по счетам за период.
   * GET /api/v1.0/Reports/trading
   */
  getReportTrading(
    params: { exchCode: string; beginDt?: string; endDt?: string; byCreation?: boolean },
    options?: RequestOptions
  ): Observable<TradeResultReport[]> {
    return this.proxy.get(this.tsName, '/Reports/trading', {
      exch_code: params.exchCode,
      begin_dt: params.beginDt,
      end_dt: params.endDt,
      by_creation: params.byCreation,
    }, options)
  }

  /**
   * Получить полный список настроек.
   * GET /api/v1.0/Settings
   */
  getSettings(options?: RequestOptions): Observable<SettingDescription[]> {
    return this.proxy.get(this.tsName, '/Settings', undefined, options)
  }

  /**
   * Изменить настройки группой.
   * POST /api/v1.0/Settings/change
   */
  changeSettings(params: { actions: SettingAction[] }): Observable<void> {
    return this.proxy.post(this.tsName, '/Settings/change', convertKeysCamelToSnake(params.actions))
  }

  /**
   * Получить список общих настроек счетов подразделений.
   * GET /api/v1.0/Settings/common/division
   */
  getCommonDivisionSettings(options?: RequestOptions): Observable<CommonDivisionSettings> {
    return this.proxy.get(this.tsName, '/Settings/common/division', undefined, options)
  }

  /**
   * Получить список общих настроек торговых счетов.
   * GET /api/v1.0/Settings/common/trading
   */
  getCommonTradingSettings(options?: RequestOptions): Observable<CommonAccountSettings> {
    return this.proxy.get(this.tsName, '/Settings/common/trading', undefined, options)
  }

  /**
   * Получить список закрытых позиций по счету за период.
   * GET /api/v1.0/Trading/closed
   */
  getClosedPositions(
    params: { accName: string; beginDt: string; endDt: string; exchCode?: string },
    options?: RequestOptions
  ): Observable<ClosedPosition[]> {
    return this.proxy.get(this.tsName, '/Trading/closed', {
      acc_name: params.accName,
      begin_dt: params.beginDt,
      end_dt: params.endDt,
      exch_code: params.exchCode,
    }, options)
  }

  /**
   * Получить торговые результаты по бирже за сегодня.
   * GET /api/v1.0/Trading/info
   */
  getTradingInfo(params?: { exchCode?: string }, options?: RequestOptions): Observable<TradingInfo[]> {
    return this.proxy.get(this.tsName, '/Trading/info', {
      exch_code: params?.exchCode,
    }, options)
  }

  /**
   * Получить список открытых позиций по счету или совокупно по бирже.
   * GET /api/v1.0/Trading/opened
   */
  getOpenedPositions(
    params: { exchCode: string; accName?: string; aggregate?: boolean },
    options?: RequestOptions
  ): Observable<OpenedPosition[]> {
    return this.proxy.get(this.tsName, '/Trading/opened', {
      exch_code: params.exchCode,
      acc_name: params.accName,
      aggregate: params.aggregate,
    }, options)
  }

  /**
   * Получить список активных заявок счета или совокупно по бирже.
   * GET /api/v1.0/Trading/orders
   */
  getOrders(params: { exchCode: string; accName?: string }, options?: RequestOptions): Observable<Order[]> {
    return this.proxy.get(this.tsName, '/Trading/orders', {
      exch_code: params.exchCode,
      acc_name: params.accName,
    }, options)
  }

  /**
   * Получить список сделок по счету за период.
   * GET /api/v1.0/Trading/trades
   */
  getTrades(
    params: { beginDt: string; endDt: string; accName?: string; exchCode?: string; limit?: number },
    options?: RequestOptions
  ): Observable<Trade[]> {
    return this.proxy.get(this.tsName, '/Trading/trades', {
      begin_dt: params.beginDt,
      end_dt: params.endDt,
      acc_name: params.accName,
      exch_code: params.exchCode,
      limit: params.limit,
    }, options)
  }

  /**
   * Получить список сделок по позиции.
   * GET /api/v1.0/Trading/trades/{uid}
   */
  getTradesByUid(params: { uid: string }, options?: RequestOptions): Observable<Trade[]> {
    return this.proxy.get(this.tsName, `/Trading/trades/${encodeURIComponent(params.uid)}`, undefined, options)
  }

  /**
   * Получить список неизвестных сделок по бирже за период.
   * GET /api/v1.0/Trading/unknown/trades
   */
  getUnknownTrades(
    params: { beginDt: string; endDt: string; exchCode: string },
    options?: RequestOptions
  ): Observable<UnknownTrade[]> {
    return this.proxy.get(this.tsName, '/Trading/unknown/trades', {
      begin_dt: params.beginDt,
      end_dt: params.endDt,
      exch_code: params.exchCode,
    }, options)
  }
}
